import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import StarIcon from "@mui/icons-material/Star";
import {
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { ICompany } from "../../../types/company";
import ProgressButton from "../../components/Common/ProgressButton";
import companyService from "../../../service/company.service";
import FreeRatioCropper from "../../components/Common/FreeRatioCropper";
import { UserRoles } from "../../../types/auth";
import { useAppContext } from "../../../contexts/useStorageContext";
import {
  companyGasReportTypeSelection,
  GasReportTypes,
} from "../../../types/common";
import SpecialPermissionPopover from "../../components/User/SpecialPermissionPopover";
import { actionTypes } from "../../../types/state";
import { SpectraDeviceNames } from "../../../types/sample";
import CompanyAutoComplete from "../../components/Common/CompanyAutoComplete";

function CompanyDetailFormInline({
  company,
  onNewCreated,
  onUpdated,
  currentParentCompany,
}: {
  company?: ICompany;
  onNewCreated?: (created: ICompany) => void;
  onUpdated?: (updates: ICompany) => void;
  currentParentCompany?: ICompany;
}) {
  const { t } = useTranslation();
  const { state, dispatch } = useAppContext();
  const { loggedInUserInfo, myCompanyProfile, cropTypes = [] } = state;
  const isSuperAdmin = loggedInUserInfo?.role == UserRoles.superadmin;
  const [editingCompany, setEditingCompany] = React.useState<ICompany>(
    company || {
      currency: myCompanyProfile?.currency || "USD",
    }
  );
  const [isSubsidiary, setIsSubsidiary] = React.useState<boolean>(
    !!(company && company.parentCompanyId) || !!currentParentCompany
  );
  const [parentCompany, setParentCompany] = React.useState<ICompany | undefined>(
    currentParentCompany
  );
  const allCropTypes = cropTypes;
  const publicCropTypes = React.useMemo(
    () => cropTypes.filter((c) => c.isPublic),
    [cropTypes]
  );
  const eligibleCropTypesForTarget = (target: "FTI" | "NAG") => {
    const allowedIds = new Set(
      (editingCompany.allowedCropTypes || []).map((crop) => String(crop._id))
    );
    const publicIds = new Set(publicCropTypes.map((crop) => String(crop._id)));
    return allCropTypes.filter(
      (crop) =>
        !!crop._id &&
        allowedIds.has(String(crop._id)) &&
        !publicIds.has(String(crop._id)) &&
        !!crop.availablePredictionTargets?.includes(target)
    );
  };
  const predictionTargetBulkState = (target: "FTI" | "NAG") => {
    const eligible = eligibleCropTypesForTarget(target);
    if (!eligible.length) {
      return { checked: false, indeterminate: false, disabled: true };
    }
    const enabledCount = eligible.filter((crop) =>
      editingCompany.allowedPredictionTargetsForCropTypes
        ?.find((entry) => String(entry.cropTypeId) === String(crop._id))
        ?.predictionTargets?.includes(target)
    ).length;
    return {
      checked: enabledCount === eligible.length,
      indeterminate: enabledCount > 0 && enabledCount < eligible.length,
      disabled: false,
    };
  };
  const setPredictionTargetForEligibleCropTypes = (
    target: "FTI" | "NAG",
    enabled: boolean
  ) => {
    const eligibleIds = new Set(
      eligibleCropTypesForTarget(target).map((crop) => String(crop._id))
    );
    const current = editingCompany.allowedPredictionTargetsForCropTypes || [];
    const next = current.map((entry) => {
      if (!eligibleIds.has(String(entry.cropTypeId))) return entry;
      const predictionTargets = entry.predictionTargets || [];
      const hasTarget = predictionTargets.includes(target);
      if ((enabled && hasTarget) || (!enabled && !hasTarget)) return entry;
      return {
        ...entry,
        predictionTargets: enabled
          ? [...predictionTargets, target]
          : predictionTargets.filter((item) => item !== target),
      };
    });
    if (enabled) {
      eligibleIds.forEach((cropTypeId) => {
        if (!next.some((entry) => String(entry.cropTypeId) === cropTypeId)) {
          next.push({ cropTypeId, predictionTargets: [target] });
        }
      });
    }
    setEditingCompany({
      ...editingCompany,
      allowedPredictionTargetsForCropTypes: next,
    });
  };
  const ftiBulkState = predictionTargetBulkState("FTI");
  const nagBulkState = predictionTargetBulkState("NAG");
  const [processing, setProcessing] = React.useState(false);
  const [openCropTypesDialog, setOpenCropTypesDialog] = React.useState(false);
  const [openImageUploadDialog, setOpenImageUploadDialog] = React.useState(false);

  const companyForSave = (): ICompany => {
    if (!isSuperAdmin) return { ...editingCompany };
    const allowedGasReportTypes = companyGasReportTypeSelection(editingCompany);
    return {
      ...editingCompany,
      allowedGasReportTypes,
      defaultGasReportType: allowedGasReportTypes[0],
    };
  };

  const handleCreate = async (vendorCompanyId?: string) => {
    setProcessing(true);
    const result = await companyService.addNewCompany({
      ...companyForSave(),
      vendorCompanyId,
    });
    if (result?.data) {
      onNewCreated && onNewCreated(result.data);
    }
    setProcessing(false);
  };

  const handleUpdate = async () => {
    if (!company) return;
    setProcessing(true);
    const savedCompany = { ...companyForSave(), _id: company._id };
    const updateInfo = { ...savedCompany };
    delete updateInfo._id;
    const result = await companyService.updateCompany(updateInfo, company._id);
    if (result?.data) {
      setEditingCompany(savedCompany);
      onUpdated && onUpdated(savedCompany);
      if (loggedInUserInfo?.company?._id == company._id) {
        dispatch!({
          type: actionTypes.setMyCompanyProfile,
          value: savedCompany,
        });
      }
    }
    setProcessing(false);
  };

  React.useEffect(() => {
    if (company) {
      setEditingCompany(company);
      setIsSubsidiary(!!company.parentCompanyId);
    }
  }, [company]);

  React.useEffect(() => {
    if (!company && currentParentCompany) {
      setIsSubsidiary(true);
      setParentCompany(currentParentCompany);
      setEditingCompany((prev) => ({
        ...prev,
        parentCompanyId: currentParentCompany._id,
      }));
    }
  }, [company, currentParentCompany]);

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: "150px",
                maxHeight: "200px",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "#152033" : "#f3f3f3",
                borderRadius: "5px",
                backgroundImage: `url(${editingCompany.logoUrl || ""})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                backgroundPosition: "center",
              }}
            >
              <IconButton
                sx={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  backgroundColor: "#0003",
                  color: "#fff",
                  "&:hover": { backgroundColor: "primary.main", color: "#fff" },
                }}
                onClick={() => setOpenImageUploadDialog(true)}
              >
                <CameraAltIcon />
              </IconButton>
            </Box>
          </Box>
          <Stack spacing={3} sx={{ flex: 1 }}>
            <TextField
              label={t("Company Name")}
              value={editingCompany.name || ""}
              onChange={(e) =>
                setEditingCompany({ ...editingCompany, name: e.target.value })
              }
              placeholder={t("Company Name")}
              fullWidth
              size="small"
            />
            <TextField
              label={t("Contact Email")}
              value={editingCompany.email || ""}
              onChange={(e) =>
                setEditingCompany({ ...editingCompany, email: e.target.value })
              }
              placeholder={t("Contact Email")}
              fullWidth
              size="small"
            />
            <TextField
              label={t("Contact Phone Number")}
              value={editingCompany.phone || ""}
              onChange={(e) =>
                setEditingCompany({ ...editingCompany, phone: e.target.value })
              }
              placeholder={t("Phone Number")}
              fullWidth
              size="small"
            />
          </Stack>
        </Stack>
        <TextField
          label={t("Location")}
          value={editingCompany.address || ""}
          onChange={(e) =>
            setEditingCompany({ ...editingCompany, address: e.target.value })
          }
          placeholder={t("Address")}
          fullWidth
          size="small"
        />
        <TextField
          label={t("Company Logo Image URL")}
          value={editingCompany.logoUrl || ""}
          onChange={(e) =>
            setEditingCompany({ ...editingCompany, logoUrl: e.target.value })
          }
          placeholder={t("Logo URL")}
          fullWidth
          size="small"
        />
        <Stack direction="row" spacing={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isSubsidiary}
                onChange={(e, checked) => {
                  setIsSubsidiary(checked);
                  if (!checked) {
                    setParentCompany(undefined);
                    setEditingCompany({
                      ...editingCompany,
                      parentCompanyId: undefined,
                    });
                  }
                }}
              />
            }
            label={t("Subsidiary of other company")}
          />
          {isSubsidiary ? (
            <div style={{ flex: 1 }}>
              <CompanyAutoComplete
                disabledIds={[editingCompany._id || ""]}
                enabled
                definedVendor={myCompanyProfile}
                isClientAutoComplete
                value={parentCompany}
                onChange={(selected) => {
                  setParentCompany(selected || undefined);
                  setEditingCompany({
                    ...editingCompany,
                    parentCompanyId: selected?._id,
                  });
                }}
                fullWidth
                label={t("Parent Company")}
              />
            </div>
          ) : null}
        </Stack>
        {isSuperAdmin ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              <Box sx={{ mr: 1, ml: 1 }}>Allowed Crop Types:</Box>
              {publicCropTypes?.map((c) => {
                return (
                  <Chip
                    variant="outlined"
                    label={
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {c.name}
                        <Box sx={{ color: "success.main", ml: 1 }}>Public</Box>
                      </div>
                    }
                    key={c._id}
                    sx={{ mr: "4px", my: "2px" }}
                  />
                );
              })}
              {editingCompany.allowedCropTypes
                ?.filter(
                  (c) => !publicCropTypes?.map((cc) => cc._id)?.includes(c._id)
                )
                .map((c) => {
                  return (
                    <Chip
                      variant="outlined"
                      label={`${c.name} / ${
                        editingCompany.allowedPredictionTargetsForCropTypes
                          ?.find((cc) => cc.cropTypeId === c._id)
                          ?.predictionTargets?.join(", ") || "- - -"
                      }`}
                      key={c._id}
                      sx={{ mr: "4px", my: "2px" }}
                      // onDelete={() => {
                      //   setEditingCompany({
                      //     ...editingCompany,
                      //     allowedCropTypes: editingCompany.allowedCropTypes?.filter(
                      //       (cc) => cc._id !== c._id
                      //     ),
                      //     allowedPredictionTargetsForCropTypes:
                      //       editingCompany.allowedPredictionTargetsForCropTypes?.filter(
                      //         (cc) => cc.cropTypeId !== c._id
                      //       ),
                      //   });
                      // }}
                    />
                  );
                })}
              <IconButton
                size="small"
                onClick={() => setOpenCropTypesDialog(true)}
              >
                <EditIcon />
              </IconButton>
              <Dialog
                open={openCropTypesDialog}
                onClose={() => setOpenCropTypesDialog(false)}
                maxWidth="sm"
                fullWidth
                scroll="paper"
              >
                <Box
                  p={2}
                  pb={0}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6">{t("Allowed Crop Types")}</Typography>
                  <IconButton onClick={() => setOpenCropTypesDialog(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box sx={{ maxHeight: "70vh", overflow: "auto" }}>
                    {allCropTypes?.length ? (
                      <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 2,
                          py: 0.5,
                          borderBottom: "solid 1px #ddd",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          backgroundColor: "background.paper",
                        }}
                      >
                        <Box sx={{ width: 34, mr: 2 }} />
                        <Box sx={{ flex: 1, mr: 2, fontWeight: 600 }}>
                          {t("All")}
                        </Box>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            width: "180px",
                            marginLeft: "24px",
                          }}
                        >
                          <FormControlLabel
                            disabled={ftiBulkState.disabled}
                            control={
                              <Checkbox
                                checked={ftiBulkState.checked}
                                indeterminate={ftiBulkState.indeterminate}
                                onChange={(_event, checked) =>
                                  setPredictionTargetForEligibleCropTypes(
                                    "FTI",
                                    checked
                                  )
                                }
                              />
                            }
                            label="FTI"
                          />
                          <FormControlLabel
                            disabled={nagBulkState.disabled}
                            control={
                              <Checkbox
                                checked={nagBulkState.checked}
                                indeterminate={nagBulkState.indeterminate}
                                onChange={(_event, checked) =>
                                  setPredictionTargetForEligibleCropTypes(
                                    "NAG",
                                    checked
                                  )
                                }
                              />
                            }
                            label="NAG"
                          />
                        </div>
                      </Box>
                      {allCropTypes.map((c, i) => {
                        return (
                          <MenuItem
                            key={c._id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              borderTop: i != 0 ? "solid 1px #ddd" : undefined,
                            }}
                          >
                            <IconButton
                              disabled={publicCropTypes
                                ?.map((cc) => cc._id)
                                ?.includes(c._id)}
                              sx={{ mr: 2 }}
                              size="small"
                              onClick={() => {
                                if (
                                  editingCompany.allowedCropTypes
                                    ?.map((cc) => cc._id)
                                    ?.includes(c._id)
                                ) {
                                  setEditingCompany({
                                    ...editingCompany,
                                    allowedCropTypes:
                                      editingCompany.allowedCropTypes?.filter(
                                        (cc) => cc._id !== c._id
                                      ),
                                    allowedPredictionTargetsForCropTypes:
                                      editingCompany.allowedPredictionTargetsForCropTypes?.filter(
                                        (cc) => cc.cropTypeId !== c._id
                                      ),
                                  });
                                } else {
                                  setEditingCompany({
                                    ...editingCompany,
                                    allowedCropTypes: [
                                      ...(editingCompany.allowedCropTypes || []),
                                      c,
                                    ],
                                  });
                                }
                              }}
                            >
                              {[
                                ...(editingCompany.allowedCropTypes || []),
                                ...(publicCropTypes || []),
                              ]
                                ?.map((cc) => cc._id)
                                ?.includes(c._id) ? (
                                <CheckCircleIcon color="success" />
                              ) : (
                                <RadioButtonUncheckedIcon />
                              )}
                            </IconButton>
                            <Box sx={{ flex: 1, mr: 2 }}>{c.name}</Box>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                width: "180px",
                                marginLeft: "24px",
                              }}
                            >
                              {c.availablePredictionTargets?.find(
                                (t) => t == "FTI"
                              ) ? (
                                <FormControlLabel
                                  disabled={
                                    !editingCompany.allowedCropTypes
                                      ?.map((cc) => cc._id)
                                      ?.includes(c._id)
                                  }
                                  control={
                                    <Checkbox
                                      checked={
                                        publicCropTypes
                                          ?.map((cc) => cc._id)
                                          ?.includes(c._id)
                                          ? true
                                          : editingCompany.allowedPredictionTargetsForCropTypes
                                              ?.find(
                                                (cc) => cc.cropTypeId === c._id
                                              )
                                              ?.predictionTargets?.includes("FTI")
                                      }
                                      onChange={(e, checked) => {
                                        const allowedPredictionTargetsForCropTypes =
                                          editingCompany.allowedPredictionTargetsForCropTypes ||
                                          [];
                                        setEditingCompany({
                                          ...editingCompany,
                                          allowedPredictionTargetsForCropTypes:
                                            checked
                                              ? allowedPredictionTargetsForCropTypes?.find(
                                                  (cc) => cc.cropTypeId == c._id
                                                )
                                                ? allowedPredictionTargetsForCropTypes.map(
                                                    (cc) =>
                                                      cc.cropTypeId == c._id
                                                        ? {
                                                            ...cc,
                                                            predictionTargets: [
                                                              ...cc.predictionTargets,
                                                              "FTI",
                                                            ],
                                                          }
                                                        : cc
                                                  )
                                                : [
                                                    ...allowedPredictionTargetsForCropTypes,
                                                    {
                                                      cropTypeId: c._id,
                                                      predictionTargets: ["FTI"],
                                                    },
                                                  ]
                                              : allowedPredictionTargetsForCropTypes.map(
                                                  (cc) =>
                                                    cc.cropTypeId == c._id
                                                      ? {
                                                          ...cc,
                                                          predictionTargets:
                                                            cc?.predictionTargets?.filter(
                                                              (p) =>
                                                                p !== "FTI"
                                                            ),
                                                        }
                                                      : cc
                                                ),
                                        });
                                      }}
                                    />
                                  }
                                  label="FTI"
                                />
                              ) : null}
                              {c.availablePredictionTargets?.find(
                                (t) => t == "NAG"
                              ) ? (
                                <FormControlLabel
                                  disabled={
                                    !editingCompany.allowedCropTypes
                                      ?.map((cc) => cc._id)
                                      ?.includes(c._id)
                                  }
                                  control={
                                    <Checkbox
                                      checked={
                                        publicCropTypes
                                          ?.map((cc) => cc._id)
                                          ?.includes(c._id)
                                          ? true
                                          : editingCompany.allowedPredictionTargetsForCropTypes
                                              ?.find(
                                                (cc) => cc.cropTypeId === c._id
                                              )
                                              ?.predictionTargets?.includes("NAG")
                                      }
                                      onChange={(e, checked) => {
                                        const allowedPredictionTargetsForCropTypes =
                                          editingCompany.allowedPredictionTargetsForCropTypes ||
                                          [];
                                        setEditingCompany({
                                          ...editingCompany,
                                          allowedPredictionTargetsForCropTypes:
                                            checked
                                              ? allowedPredictionTargetsForCropTypes?.find(
                                                  (cc) => cc.cropTypeId == c._id
                                                )
                                                ? allowedPredictionTargetsForCropTypes.map(
                                                    (cc) =>
                                                      cc.cropTypeId == c._id
                                                        ? {
                                                            ...cc,
                                                            predictionTargets: [
                                                              ...cc.predictionTargets,
                                                              "NAG",
                                                            ],
                                                          }
                                                        : cc
                                                  )
                                                : [
                                                    ...allowedPredictionTargetsForCropTypes,
                                                    {
                                                      cropTypeId: c._id,
                                                      predictionTargets: ["NAG"],
                                                    },
                                                  ]
                                              : allowedPredictionTargetsForCropTypes.map(
                                                  (cc) =>
                                                    cc.cropTypeId == c._id
                                                      ? {
                                                          ...cc,
                                                          predictionTargets:
                                                            cc?.predictionTargets?.filter(
                                                              (p) =>
                                                                p !== "NAG"
                                                            ),
                                                        }
                                                      : cc
                                                ),
                                        });
                                      }}
                                    />
                                  }
                                  label="NAG"
                                />
                              ) : null}
                            </div>
                          </MenuItem>
                        );
                      })}
                      </>
                    ) : (
                      <Box p={2}>No crop types</Box>
                    )}
                </Box>
              </Dialog>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                mt: 2,
                mb: 1,
              }}
            >
              <Box sx={{ mr: 1, ml: 1 }}>{t("Special Permissions")}:</Box>
              {[...(editingCompany.permissions || [])].map((permission) => (
                <Chip
                  key={permission.permissionName}
                  sx={{ mr: "4px", my: "2px" }}
                  label={`${
                    permission.permissionName
                  }: ${permission.limitedPermissions?.join(", ")||""}${
                    permission.limitedPermissions?.length ? " / " : ""
                  }${permission.read ? t("Read") : ""} ${
                    permission.write ? " / " + t("write") : ""
                  }`}
                />
              ))}
              <SpecialPermissionPopover
                dialogMaxWidth="sm"
                asDialog
                targetCompany={editingCompany}
                triggerElement={
                  <IconButton size="small">
                    <EditIcon />
                  </IconButton>
                }
                currentPermissions={editingCompany.permissions || []}
                onChangePermissions={(changed) =>
                  setEditingCompany({ ...editingCompany, permissions: changed })
                }
              />
            </Box>
            <div>
            <Box sx={{ mr: 3, ml: 1, mt:2 }}>{t("Spectra Devices")}:</Box>           
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                mb: 1,
                ml:1
              }}
            >
              {Object.keys(SpectraDeviceNames)
                ?.filter((d) => isNaN(Number(d)))
                .map((deviceName) => {
                  const deviceNameValue =
                    SpectraDeviceNames[deviceName as keyof typeof SpectraDeviceNames];
                  return (
                    <FormControlLabel
                      key={deviceName}
                      control={
                        <Checkbox
                          checked={editingCompany.spectraDeviceNames?.includes(
                            deviceNameValue
                          )}
                          onChange={(e, checked) => {
                            setEditingCompany({
                              ...editingCompany,
                              spectraDeviceNames: checked
                                ? [
                                    ...(editingCompany.spectraDeviceNames || []),
                                    deviceNameValue,
                                  ]
                                : editingCompany.spectraDeviceNames?.filter(
                                    (d) => d !== deviceNameValue
                                  ),
                            });
                          }}
                        />
                      }
                      label={deviceName}
                    />
                  );
                })}
            </Box>
            </div>
          </Box>
        ) : null}
      </Stack>
      <Divider textAlign="center" sx={{ my: 1, fontSize: "13px", color: "#888" }}>
        {t("Company Settings")}
      </Divider>
      {isSuperAdmin ? (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            select
            label={t("Permitted Gas Report Types")}
            value={companyGasReportTypeSelection(editingCompany)}
            size="small"
            helperText={t(
              "First selected type is the default. Company users see only these types after saving."
            )}
            onChange={(e) => {
              const raw = e.target.value;
              const next = (typeof raw === "string" ? raw.split(",") : raw)
                .map((value) => Number(value))
                .filter(
                  (value, index, list) =>
                    GasReportTypes.some((type) => type.value === value) &&
                    list.indexOf(value) === index
                );
              setEditingCompany({
                ...editingCompany,
                allowedGasReportTypes: next,
                defaultGasReportType: next[0],
              });
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as number[]).map((value, index) => {
                    const type = GasReportTypes.find(
                      (item) => item.value === Number(value)
                    );
                    if (!type) return null;
                    const isDefault = index === 0;
                    return (
                      <Chip
                        key={value}
                        size="small"
                        label={
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {isDefault ? (
                              <StarIcon
                                titleAccess={t("Default type")}
                                sx={{ fontSize: 16, color: "warning.main" }}
                              />
                            ) : null}
                            {type.name}
                          </Box>
                        }
                      />
                    );
                  })}
                </Box>
              ),
            }}
            sx={{
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "4px",
              },
            }}
          >
            {GasReportTypes.map((v) => {
              const selectedTypes = companyGasReportTypeSelection(editingCompany);
              const isDefault = selectedTypes[0] === v.value;
              return (
                <MenuItem key={v.value} value={v.value}>
                  <Checkbox
                    size="small"
                    checked={selectedTypes.includes(v.value)}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  {v.name}
                  {isDefault ? (
                    <StarIcon
                      fontSize="small"
                      titleAccess={t("Default type")}
                      sx={{ ml: "auto", color: "warning.main" }}
                    />
                  ) : (
                    <Box sx={{ width: 20, ml: "auto" }} />
                  )}
                </MenuItem>
              );
            })}
          </TextField>
        </Box>
      ) : null}
      <Stack direction={{ xs: "column", sm: "row" }} sx={{ mt: 2 }} spacing={3}>
        <TextField
          fullWidth
          size="small"
          label={t("Currency Code")}
          value={editingCompany.currency || ""}
          onChange={(e) =>
            setEditingCompany({ ...editingCompany, currency: e.target.value })
          }
          placeholder={t("USD by default")}
        />
      </Stack>
      <Box sx={{ mt: 2 }}>
        <Box sx={{
          padding:"2px 16px",
          background:"#ff6c0021",
          border: "solid 1px #ff6c003d",
          borderRadius:"6px"
        }}>
          <FormControlLabel
            color="warning"
            control={
              <Checkbox
                color="warning"
                checked={!!editingCompany.hideClientInfo}
                onChange={(e, checked) =>
                  setEditingCompany({ ...editingCompany, hideClientInfo: checked })
                }
              />
            }
            label={t("Hide client and farm info in samples")}
          />
        </Box>
      </Box>
      <Box
        mt={3}
        sx={{
          display: "flex",
          position: "sticky",
          bottom: 0,
          backgroundColor: "background.paper",
          py: 1,
          zIndex: 2,
        }}
      >
        <div style={{ flex: 1 }} />
        {company ? (
          <ProgressButton
            disabled={!editingCompany?.name}
            onClick={() => !processing && handleUpdate()}
            title={t("Save Changes")}
            inProgress={processing}
          />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ProgressButton
              disabled={!editingCompany?.name}
              onClick={() => !processing && handleCreate()}
              title={isSuperAdmin ? t("Add New Lab") : t("Add New Client")}
              inProgress={processing}
            />
            {isSuperAdmin && loggedInUserInfo?.company && myCompanyProfile?._id ? (
              <ProgressButton
                disabled={!editingCompany?.name}
                onClick={() => !processing && handleCreate(myCompanyProfile._id)}
                title={t(`Add New Client Of ${myCompanyProfile?.name}`)}
                inProgress={processing}
              />
            ) : null}
          </div>
        )}
      </Box>
      {openImageUploadDialog && (
        <FreeRatioCropper
          onSave={(url) => {
            setEditingCompany({ ...editingCompany, logoUrl: url });
            setOpenImageUploadDialog(false);
          }}
          open
          onClose={() => setOpenImageUploadDialog(false)}
        />
      )}
    </Box>
  );
}

export default function CompanyFormDialog({
  company,
  open,
  onClose,
  onUpdated,
  onNewCreated,
  currentParentCompany,
}: {
  company?: ICompany;
  open: boolean;
  onClose: () => void;
  onUpdated?: (updateInfo: ICompany) => void;
  onNewCreated?: (created: ICompany) => void;
  currentParentCompany?: ICompany;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <Dialog open={open} maxWidth="sm" scroll="paper">
        <Box
          sx={{
            width: "1000px",
            maxWidth: "100%",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            p={2}
            pb={0}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
              zIndex: 2,
            }}
          >
            <Typography variant="h6">
              {company ? t("Company Details") : t("New Company")}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box p={2}>
            <CompanyDetailFormInline
              company={company}
              onNewCreated={(created) => onNewCreated && onNewCreated(created)}
              onUpdated={(updateInfo) => onUpdated && onUpdated(updateInfo)}
              currentParentCompany={currentParentCompany}
            />
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
